import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Zap, Star, Clock, Tag, ChevronRight, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatModelTokenPrice } from '../utils/textUtils';
import './ModelGallery.css';

// Добавляем autoplay/background параметры для Vimeo/YouTube
const getEmbedUrl = (url) => {
  try {
    const u = new URL(url);
    u.searchParams.set('autoplay', '1');
    u.searchParams.set('muted', '1');
    u.searchParams.set('loop', '1');
    u.searchParams.set('background', '1'); // Vimeo: скрывает UI + тихий автоплей
    return u.toString();
  } catch {
    return url;
  }
};

const isVideoModel = (model) => (
  model.category === 'video_generation' || model.category === 'video_editing'
);

function ModelGallery() {
  const navigate = useNavigate();
  const location = useLocation();
  const filtersRef = useRef(null);
  const [models, setModels] = useState([]);
  const [allModels, setAllModels] = useState([]); // Все модели для клиентской фильтрации
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
  const [selectedAuthor, setSelectedAuthor] = useState('all');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedAuthors, setExpandedAuthors] = useState(false);

  const fetchModels = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      
      if (showFeaturedOnly) {
        params.is_featured = true;
      }
      
      const response = await axios.get('/api/ai-models/list', { params });
      
      setAllModels(response.data || []);
      setModels(response.data || []);
    } catch (error) {
      console.error('Error fetching models:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Не удалось загрузить модели. Пожалуйста, попробуйте позже.');
      setAllModels([]);
      setModels([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, showFeaturedOnly]);

  // Загружаем модели только при изменении категории или featured
  // НЕ загружаем, если мы на странице конкретной модели (есть slug в URL)
  useEffect(() => {
    // Проверяем, не находимся ли мы на странице конкретной модели
    const pathMatch = location.pathname.match(/\/models\/([^/]+)/);
    if (pathMatch && pathMatch[1]) {
      // Есть slug модели в URL - не загружаем все модели (модель загружается отдельно через by-slug)
      return;
    }
    fetchModels();
  }, [fetchModels, location.pathname]);

  // Закрываем выпадающие фильтры при клике/тапе вне блока фильтров
  useEffect(() => {
    const handlePointerDown = (e) => {
      if (!filtersRef.current) return;
      if (!filtersRef.current.contains(e.target)) {
        setExpandedGroups({});
        setExpandedAuthors(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, []);

  const categories = {
    all: 'Все модели',
    image_generation: 'Генерация',
    image_editing: 'Редактирование',
    enhancement: 'Улучшение',
    video_generation: 'Видео генерация',
    video_editing: 'Видео редактирование',
    music_generation: 'Музыка',
    speech_generation: 'Речь'
  };

  // Группировка категорий для раскрывающихся списков
  const categoryGroups = {
    'Фото': {
      image_generation: 'Генерация',
      image_editing: 'Редактирование',
      enhancement: 'Улучшение'
    },
    'Видео': {
      video_generation: 'Видео генерация',
      video_editing: 'Видео редактирование'
    },
    'Аудио': {
      music_generation: 'Музыка',
      speech_generation: 'Речь'
    }
  };
  
  // Функция для переключения группы (закрывает другие при открытии новой)
  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => {
      const newState = { ...prev };
      // Если группа уже открыта, закрываем её
      if (newState[groupName]) {
        delete newState[groupName];
      } else {
        // Закрываем все остальные группы и открываем выбранную
        Object.keys(newState).forEach(key => delete newState[key]);
        newState[groupName] = true;
      }
      return newState;
    });
    // Закрываем меню создателей при открытии группы категорий
    setExpandedAuthors(false);
  };
  
  // Функция для выбора категории (закрывает все выпадающие меню)
  const handleCategorySelect = (categoryKey) => {
    setSelectedCategory(categoryKey);
    // Закрываем все выпадающие меню при выборе категории
    setExpandedGroups({});
    setExpandedAuthors(false);
  };

  // Получаем список всех уникальных авторов
  const authors = React.useMemo(() => {
    const authorsSet = new Set();
    allModels.forEach(model => {
      if (model.author) {
        authorsSet.add(model.author);
      }
    });
    return Array.from(authorsSet).sort();
  }, [allModels]);

  // Клиентская фильтрация по поисковому запросу и автору
  const filteredModels = React.useMemo(() => {
    let filtered = models;
    
    // Фильтр по автору
    if (selectedAuthor !== 'all') {
      filtered = filtered.filter(model => model.author === selectedAuthor);
    }
    
    // Фильтр по поисковому запросу
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(model => 
        (model.name?.toLowerCase() || '').includes(query) ||
        (model.display_name?.toLowerCase() || '').includes(query) ||
        (model.description?.toLowerCase() || '').includes(query) ||
        (model.short_description?.toLowerCase() || '').includes(query) ||
        model.tags?.some(tag => (tag?.toLowerCase() || '').includes(query)) ||
        (model.author?.toLowerCase() || '').includes(query)
      );
    }
    
    return filtered;
  }, [models, searchQuery, selectedAuthor]);

  if (loading) {
    return (
      <div className="model-gallery-loading">
        <div className="spinner"></div>
        <p>Загрузка моделей...</p>
      </div>
    );
  }

  if (!loading && models.length === 0 && !searchQuery && selectedCategory === 'all') {
    return (
      <div className="model-gallery">
        <div className="gallery-header">
          <h1>AI Модели</h1>
          <p className="subtitle">Модели временно недоступны</p>
        </div>
        <div className="no-models">
          <p>Модели временно недоступны. Пожалуйста, попробуйте обновить страницу или повторить попытку позже.</p>
          <button 
            className="retry-btn"
            onClick={fetchModels}
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="model-gallery">
      <div className="gallery-header">
        <div className="header-title">
          <h1>AI Модели</h1>
          <p className="header-subtitle">
            Выберите модель для работы с изображениями и видео
          </p>
        </div>

        <div className="gallery-filters" ref={filtersRef}>
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Поиск моделей..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="category-filters">
            <button
              className={`category-btn ${selectedCategory === 'all' ? 'active' : ''}`}
              onClick={() => handleCategorySelect('all')}
            >
              Все модели
            </button>
            
            {Object.entries(categoryGroups).map(([groupName, groupCategories]) => {
              // Показываем раскрывающееся меню только если категорий больше 1
              const categoryCount = Object.keys(groupCategories).length;
              const shouldShowDropdown = categoryCount > 1;
              
              if (!shouldShowDropdown) {
                // Если только 1 категория, показываем как обычную кнопку
                const singleCategoryKey = Object.keys(groupCategories)[0];
                const singleCategoryLabel = Object.values(groupCategories)[0];
                return (
                  <button
                    key={groupName}
                    className={`category-btn ${selectedCategory === singleCategoryKey ? 'active' : ''}`}
                    onClick={() => handleCategorySelect(singleCategoryKey)}
                  >
                    {singleCategoryLabel}
                  </button>
                );
              }
              
              // Если категорий больше 1, показываем раскрывающееся меню
              return (
                <div key={groupName} className="category-group">
                  <button
                    className={`category-group-header ${expandedGroups[groupName] ? 'expanded' : ''}`}
                    onClick={() => toggleGroup(groupName)}
                  >
                    {groupName}
                    <ChevronRight 
                      size={16} 
                      style={{ 
                        transform: expandedGroups[groupName] ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s'
                      }} 
                    />
                  </button>
                  {expandedGroups[groupName] && (
                    <div className="category-group-items">
                      {Object.entries(groupCategories).map(([key, label]) => (
                        <button
                          key={key}
                          className={`category-btn ${selectedCategory === key ? 'active' : ''}`}
                          onClick={() => handleCategorySelect(key)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            
            {/* Фильтр по создателям - выпадающее меню */}
            {authors.length > 0 && (
              <div className="category-group">
                <button
                  className={`category-group-header ${expandedAuthors ? 'expanded' : ''}`}
                  onClick={() => {
                    setExpandedAuthors(!expandedAuthors);
                    // Закрываем все остальные группы при открытии создателей
                    setExpandedGroups({});
                  }}
                >
                  {selectedAuthor === 'all' ? 'Создатель' : selectedAuthor}
                  <ChevronRight 
                    size={16} 
                    style={{ 
                      transform: expandedAuthors ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s'
                    }} 
                  />
                </button>
                {expandedAuthors && (
                  <div className="category-group-items">
                    <button
                      className={`category-btn ${selectedAuthor === 'all' ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedAuthor('all');
                        setExpandedAuthors(false);
                      }}
                    >
                      Все создатели
                    </button>
                    {authors.map(author => (
                      <button
                        key={author}
                        className={`category-btn ${selectedAuthor === author ? 'active' : ''}`}
                        onClick={() => {
                          setSelectedAuthor(author);
                          setExpandedAuthors(false);
                        }}
                      >
                        {author}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <label className="featured-toggle">
            <input
              type="checkbox"
              checked={showFeaturedOnly}
              onChange={(e) => setShowFeaturedOnly(e.target.checked)}
            />
            <Star size={16} />
            Рекомендуемые
          </label>
        </div>
      </div>

      {filteredModels.length === 0 ? (
        <div className="no-models">
          <p>Модели не найдены</p>
        </div>
      ) : (
        <div className="models-grid">
          {filteredModels.map((model) => (
            <Link
              key={model.id}
              to={`/models/${model.slug}`}
              className={`model-card ${model.is_featured ? 'featured' : ''}`}
              onClick={(e) => {
                // Игнорируем клик, если кликнули на кнопку "Выбрать модель"
                if (e.target.closest('.select-btn')) {
                  e.preventDefault();
                  return;
                }
                // Поддержка Ctrl/Cmd+Click для открытия в новой вкладке
                if (e.ctrlKey || e.metaKey) {
                  e.preventDefault();
                  window.open(`/models/${model.slug}`, '_blank');
                }
              }}
              onAuxClick={(e) => {
                // Средняя кнопка мыши (колесико) или Fn + клик на macOS - открываем в новой вкладке
                if (e.button === 1) {
                  // Средняя кнопка мыши
                  e.preventDefault();
                  window.open(`/models/${model.slug}`, '_blank');
                } else if (e.button === 2) {
                  // Правая кнопка мыши (Fn + клик на macOS может эмулировать это)
                  // Открываем в новой вкладке вместо показа контекстного меню
                  e.preventDefault();
                  window.open(`/models/${model.slug}`, '_blank');
                }
              }}
              onMouseDown={(e) => {
                // Средняя кнопка мыши (колесико) - открываем в новой вкладке (fallback)
                if (e.button === 1) {
                  e.preventDefault();
                  window.open(`/models/${model.slug}`, '_blank');
                }
              }}
              onContextMenu={(e) => {
                // На macOS Fn + клик на трекпаде может вызвать onContextMenu
                // Открываем в новой вкладке вместо показа контекстного меню
                // Это позволяет использовать Fn + клик для открытия в новой вкладке
                e.preventDefault();
                window.open(`/models/${model.slug}`, '_blank');
              }}
            >
              {model.is_featured && (
                <div className="featured-badge">
                  <Star size={14} fill="currentColor" />
                  Рекомендуем
                </div>
              )}

              <div className="model-cover">
                {model.cover_image_url ? (
                  (() => {
                    const coverLower = model.cover_image_url?.toLowerCase() || '';
                    const isEmbed = coverLower.includes('player.vimeo.com') || coverLower.includes('youtube.com/embed') || coverLower.includes('youtu.be');
                    const isVideo = !isEmbed && (coverLower.includes('mime_type=video') || /\.(mp4|mov|webm|gif)(\?|$|#)/i.test(coverLower));
                    if (isEmbed) {
                      return (
                        <iframe
                          src={getEmbedUrl(model.cover_image_url)}
                          frameBorder="0"
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                          title={model.display_name}
                          style={{ width: '100%', height: '100%', pointerEvents: 'none', border: 'none' }}
                        />
                      );
                    }
                    return isVideo ? (
                      <video 
                        src={model.cover_image_url} 
                        muted
                        loop
                        playsInline
                        autoPlay
                        preload="auto"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                        onLoadedData={(e) => {
                          e.target.play().catch(err => {
                            console.log('Autoplay prevented:', err);
                          });
                        }}
                      />
                    ) : (
                      <img 
                        src={model.cover_image_url} 
                        alt={model.display_name}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    );
                  })()
                ) : null}
                <div className="cover-placeholder" style={{display: model.cover_image_url ? 'none' : 'flex'}}>
                  <Zap size={48} />
                </div>
                
                <div className="model-overlay">
                  <button 
                    className="select-btn"
                    onClick={(e) => {
                      // Предотвращаем всплытие события до Link
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(`/models/${model.slug}`);
                    }}
                  >
                    Выбрать модель
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>

              <div className="model-info">
                <div className="model-header">
                  <h3 className="model-name">{model.display_name}</h3>
                  <div 
                    className="model-category"
                  >
                    {categories[model.category] || model.category}
                  </div>
                </div>

                <p className="model-description">
                  {model.short_description}
                </p>

                <div className="model-tags">
                  {model.tags && model.tags.slice(0, 3).map((tag, idx) => (
                    <span key={idx} className="tag">
                      <Tag size={12} />
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="model-footer">
                  <div className="model-stats">
                    {!isVideoModel(model) && (
                      <div className="stat">
                        <Zap size={14} />
                        <span className="cost">
                          {formatModelTokenPrice(model)}
                        </span>
                      </div>
                    )}
                    <div className="stat">
                      <Clock size={14} />
                      <span>~{model.estimated_time_seconds}с</span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default ModelGallery;


